
/**
 * DSP Payload Schema Guard - V2.3 (Timing Integrity)
 */

import { AnalyzeFullResult } from "./dspMath";

export type DspSchemaCheck = {
  ok: boolean;
  reasons: string[];
  sanitized: AnalyzeFullResult;
};

const isFiniteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const safeNumber = (v: unknown, fallback: number): number => (isFiniteNum(v) ? v : fallback);

export function validateDspPayload(raw: unknown): DspSchemaCheck {
  const reasons: string[] = [];
  const dsp = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  const requiredM = ['rms_db', 'onset_count', 'transient_ratio', 'stability', 'peakedness', 'clip_ratio'];
  for (const k of requiredM) {
    if (!isFiniteNum(dsp[k])) reasons.push(`DSP_METRIC_${k.toUpperCase()}_INVALID`);
  }

  const clarity = safeNumber(dsp.chroma_clarity, 0.5);

  const sanitized: AnalyzeFullResult = {
    chroma: (Array.isArray(dsp.chroma) && dsp.chroma.length === 12) ? dsp.chroma as number[] : new Array(12).fill(0),
    chromaHistory: Array.isArray(dsp.chromaHistory) ? dsp.chromaHistory as number[][] : [],
    chroma_entropy: safeNumber(dsp.chroma_entropy, 0.5),
    chroma_clarity: clarity,
    chroma_clarity_score: safeNumber(dsp.chroma_clarity_score, Math.round(clarity * 100)),
    onset_count: safeNumber(dsp.onset_count, 0),
    onsetTimesMs: Array.isArray(dsp.onsetTimesMs) ? dsp.onsetTimesMs as number[] : [],
    onsetIntensities: Array.isArray(dsp.onsetIntensities) ? dsp.onsetIntensities as number[] : [],
    refOnsetTimesMs: Array.isArray(dsp.refOnsetTimesMs) ? dsp.refOnsetTimesMs as number[] : [],
    refOnsetIntensities: Array.isArray(dsp.refOnsetIntensities) ? dsp.refOnsetIntensities as number[] : [],
    timingDeviationMs: Array.isArray(dsp.timingDeviationMs) ? dsp.timingDeviationMs as number[] : [],
    rms_db: safeNumber(dsp.rms_db, -240),
    confidence: safeNumber(dsp.confidence, 0.0),
    stability: safeNumber(dsp.stability, 0.0),
    noise_ratio: safeNumber(dsp.noise_ratio, 1.0),
    clip_ratio: safeNumber(dsp.clip_ratio, 0.0),
    transient_ratio: safeNumber(dsp.transient_ratio, 0.0),
    peakedness: safeNumber(dsp.peakedness, 0.0),
    stuEnvelope: Array.isArray(dsp.stuEnvelope) ? dsp.stuEnvelope as number[] : [],
    refEnvelope: Array.isArray(dsp.refEnvelope) ? dsp.refEnvelope as number[] : [],
    stuBands: (dsp.stuBands as any) || { low: 0, mid: 0, high: 0 },
    refBands: (dsp.refBands as any) || { low: 0, mid: 0, high: 0 },
    stuFingerprint: Array.isArray(dsp.stuFingerprint) ? dsp.stuFingerprint as number[] : [],
    refFingerprint: Array.isArray(dsp.refFingerprint) ? dsp.refFingerprint as number[] : [],
    ghostNotes: Array.isArray(dsp.ghostNotes) ? dsp.ghostNotes as any[] : [],
    targetBpm: safeNumber(dsp.targetBpm, 120),
    sampleRate: safeNumber(dsp.sampleRate, 44100),
    refChromaHistory: Array.isArray(dsp.refChromaHistory) ? dsp.refChromaHistory as number[][] : undefined,
    hardwareLagMs: safeNumber(dsp.hardwareLagMs, 0)
  };

  return { ok: reasons.length === 0, reasons, sanitized };
}
